import { apiClient } from './axios';

export interface CreatePatientDto {
  cnp: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  county?: string;
  programType: 'DERMATO' | 'HEPATO' | 'BOTH';
  personalDataConsent: boolean;
  medicalDataConsent: boolean;
  consentIpAddress?: string;
  consentUserAgent?: string;
}

export interface Patient {
  id: string;
  cnp: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'MALE' | 'FEMALE';
  sex: 'M' | 'F';
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  programType: 'DERMATO' | 'HEPATO' | 'BOTH';
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  /** Numărul de registru (HSK-…), alocat la includerea în registru; poate lipsi */
  registryNumber?: string | null;
  assignedDoctorId: string | null;
  createdAt: string;
  updatedAt: string;
}

const unwrapResponse = <T>(responseData: any): T => responseData?.data ?? responseData;

export const patientApi = {
  /**
   * Create new patient
   */
  async create(data: CreatePatientDto): Promise<Patient> {
    const response = await apiClient.post('/patients', data);
    return normalizePatient(unwrapResponse<Patient>(response.data));
  },

  /**
   * Get patient by ID
   */
  async getById(id: string): Promise<Patient> {
    const response = await apiClient.get(`/patients/${id}`);
    return normalizePatient(unwrapResponse<Patient>(response.data));
  },

  /**
   * List patients
   */
  async list(params?: {
    page?: number;
    limit?: number;
    search?: string;
    cnp?: string;
    email?: string;
    name?: string;
    programType?: string;
    status?: string;
  }): Promise<{ patients: Patient[]; total: number }> {
    const response = await apiClient.get('/patients', { params });
    const payload = response.data?.data && !Array.isArray(response.data.data)
      ? response.data.data
      : response.data;
    const patients = Array.isArray(payload) ? payload : payload.patients || payload.data || [];

    return {
      patients: patients.map(normalizePatient),
      total: payload.total ?? payload.meta?.total ?? patients.length,
    };
  },

  async findByCnp(cnp: string): Promise<Patient | null> {
    const { patients } = await this.list({ cnp, limit: 1 });
    return patients.find((patient) => patient.cnp === cnp) ?? null;
  },
};

function normalizePatient(patient: any): Patient {
  return {
    ...patient,
    sex: patient.sex ?? (patient.gender === 'FEMALE' ? 'F' : 'M'),
  };
}
